import { PLANS, type Plan } from "@cpos/shared/entitlements";
import type { MerchantFilters } from "./types";

/** URL parameters the merchants list reads, one per filter they drive. */
export const LIST_PARAM_KEYS = [
  "signedUpFrom",
  "signedUpTo",
  "proSinceFrom",
  "proSinceTo",
  "plan",
] as const;

export type ListParamKey = (typeof LIST_PARAM_KEYS)[number];

/** The filters the URL can carry. */
export type UrlFilters = Pick<MerchantFilters, ListParamKey>;

const EMPTY: UrlFilters = {
  signedUpFrom: null,
  signedUpTo: null,
  proSinceFrom: null,
  proSinceTo: null,
  plan: null,
};

const LOCAL_TIME = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d))?/;
const FRACTION = /^\.\d{1,9}/;
const OFFSET = /^(Z|[+-]\d\d:?\d\d)$/;

/**
 * An ISO-8601 instant with a date, a time and an explicit `Z` or numeric
 * offset, else null. Date-only and offset-less strings are rejected because
 * they would be read in the viewer's local zone. A `+` in a query string
 * decodes to a space, so `…T20:00+05:00` arrives as `…T20:00 05:00` and is
 * restored first. Out-of-range fields such as `02-30` or `24:00` are
 * rejected rather than rolled over, as `Date.parse` would.
 */
function parseInstant(value: string | null): number | null {
  if (!value) return null;
  const text = value.replace(/ (\d{2}:?\d{2})$/, "+$1");
  const time = LOCAL_TIME.exec(text);
  if (!time) return null;
  const [year, month, day, hour, minute, second] = time
    .slice(1)
    .map((part) => Number(part ?? 0));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  const rest = text.slice(time[0].length);
  const fraction = FRACTION.exec(rest);
  if (!OFFSET.test(rest.slice(fraction?.[0].length ?? 0))) return null;
  const ms = Date.parse(text);
  return Number.isNaN(ms) ? null : ms;
}

function parsePlan(value: string | null): Plan | null {
  return (PLANS as readonly string[]).includes(value ?? "")
    ? (value as Plan)
    : null;
}

/**
 * Reads the filters a link carries. A missing or malformed parameter yields
 * null for that filter and never throws, so a stale or hand-edited link still
 * opens the list.
 */
export function parseListParams(params: URLSearchParams): UrlFilters {
  return {
    signedUpFrom: parseInstant(params.get("signedUpFrom")),
    signedUpTo: parseInstant(params.get("signedUpTo")),
    proSinceFrom: parseInstant(params.get("proSinceFrom")),
    proSinceTo: parseInstant(params.get("proSinceTo")),
    plan: parsePlan(params.get("plan")),
  };
}

/** No URL-driven filters set. */
export function emptyUrlFilters(): UrlFilters {
  return { ...EMPTY };
}

/** A removable chip: what to show and which URL parameters it owns. */
export interface FilterChip {
  id: "signedUp" | "proSince" | "plan";
  label: string;
  keys: ListParamKey[];
}

const ET = "America/New_York";

const YEAR_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: ET,
  year: "numeric",
});
const SAME_YEAR_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: ET,
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const OTHER_YEAR_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: ET,
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** `Oct 27, 11:00 AM` in Eastern Time, with the year when it is not the current one. */
export function formatEt(ms: number, now: number = Date.now()): string {
  const sameYear = YEAR_FORMAT.format(ms) === YEAR_FORMAT.format(now);
  return (sameYear ? SAME_YEAR_FORMAT : OTHER_YEAR_FORMAT).format(ms);
}

function windowLabel(
  name: string,
  from: number | null,
  to: number | null,
): string {
  if (from !== null && to !== null)
    return `${name}: ${formatEt(from)} – ${formatEt(to)} ET`;
  if (from !== null) return `${name}: from ${formatEt(from)} ET`;
  return `${name}: before ${formatEt(to as number)} ET`;
}

/** One chip per active URL-driven filter. */
export function activeChips(filters: UrlFilters): FilterChip[] {
  const chips: FilterChip[] = [];
  if (filters.signedUpFrom !== null || filters.signedUpTo !== null) {
    chips.push({
      id: "signedUp",
      label: windowLabel("Signed up", filters.signedUpFrom, filters.signedUpTo),
      keys: ["signedUpFrom", "signedUpTo"],
    });
  }
  if (filters.proSinceFrom !== null || filters.proSinceTo !== null) {
    chips.push({
      id: "proSince",
      label: windowLabel("Pro since", filters.proSinceFrom, filters.proSinceTo),
      keys: ["proSinceFrom", "proSinceTo"],
    });
  }
  if (filters.plan !== null) {
    chips.push({
      id: "plan",
      label: `Plan: ${filters.plan === "pro" ? "Pro" : "Free"}`,
      keys: ["plan"],
    });
  }
  return chips;
}

/** The URL parameters left after a chip is removed; unrelated parameters are kept. */
export function paramsWithoutChip(
  params: URLSearchParams,
  chip: FilterChip,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of chip.keys) next.delete(key);
  return next;
}

/** True when `iso` falls in `[from, to)`; either bound may be open. A missing time never matches a bounded window. */
export function inWindow(
  iso: string | null | undefined,
  from: number | null,
  to: number | null,
): boolean {
  if (from === null && to === null) return true;
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return false;
  return (from === null || ms >= from) && (to === null || ms < to);
}
